/**
 * Draft league rules: players, the draft order, picks and budgets, Tera Captains, the season schedule, results,
 * standings, trades and free agents. Nothing here knows about Showdown or the web: the Showdown draft plugin and
 * draft.ogreclan.com both run these same rules on the same league data.
 */
import { toID } from '@ogreclan/pokemon-core';
import { LeagueError } from './errors.js';
import { resolveSpecies, speciesName } from './names.js';
import type { DraftLeague, DraftMatch, DraftPick, DraftPlayer, DraftTrade, MatchResult, Standing, TradeStatus } from './types.js';
import { count, sortBy } from './util.js';

export const DEFAULT_SETTINGS = {
	type: 'points',
	budget: 100,
	picks: 10,
	minPoints: 1,
	maxPoints: 20,
	snake: true,
	shuffle: false,
	pickTimer: 0,
	tera: false,
	teraCaptains: 2,
	teraCaptainMaxPoints: 0,
	freeAgents: 3,
	trades: 3,
	weeks: 7,
	bestOf: 1,
} as const;

/** A new league, in its setup phase */
export function createLeague(roomid: string, format: string): DraftLeague {
	return {
		roomid,
		phase: 'setup',
		settings: { format, ...DEFAULT_SETTINGS },
		pool: {},
		players: [],
		picks: [],
		pickNumber: 0,
		pickDeadline: null,
		schedule: [],
		week: 0,
		trades: [],
		nextTradeId: 1,
		transactions: [],
	};
}

/* Players */

export function getPlayer(league: DraftLeague, name: string) {
	const id = toID(name);
	return league.players.find(player => player.id === id) || null;
}

export function requirePlayer(league: DraftLeague, name: string) {
	const player = getPlayer(league, name);
	if (!player) throw new LeagueError(`${name.trim() || "That user"} isn't playing in this draft.`);
	return player;
}

export function addPlayer(league: DraftLeague, name: string) {
	name = name.trim();
	const id = toID(name);
	if (!id || id.length > 18) throw new LeagueError(`"${name}" isn't a valid username.`);
	if (getPlayer(league, id)) throw new LeagueError(`${name} is already in the draft.`);
	const player: DraftPlayer = {
		id, name, roster: [], captains: [], teams: {}, freeAgentsUsed: 0, tradesUsed: 0, owedPicks: 0,
	};
	league.players.push(player);
	return player;
}

export function removePlayer(league: DraftLeague, name: string) {
	const player = requirePlayer(league, name);
	if (league.phase !== 'setup') throw new LeagueError(`Players can only be removed before the draft starts.`);
	league.players.splice(league.players.indexOf(player), 1);
	return player;
}

/** Changes a player's username, keeping their roster, teams and results */
export function renamePlayer(league: DraftLeague, oldName: string, newName: string) {
	const player = requirePlayer(league, oldName);
	const newId = toID(newName);
	if (!newId || newId.length > 18) throw new LeagueError(`"${newName}" isn't a valid username.`);
	if (newId !== player.id && getPlayer(league, newId)) throw new LeagueError(`${newName} is already in the draft.`);
	const oldId = player.id;
	player.id = newId;
	player.name = newName.trim();
	for (const pick of league.picks) if (pick.player === oldId) pick.player = newId;
	for (const trade of league.trades) {
		if (trade.from === oldId) trade.from = newId;
		if (trade.to === oldId) trade.to = newId;
	}
	for (const week of league.schedule) {
		for (const match of week) {
			if (match.p1 === oldId) match.p1 = newId;
			if (match.p2 === oldId) match.p2 = newId;
			if (match.result?.winner === oldId) match.result.winner = newId;
			for (const game of match.result?.games || []) {
				if (game.winner === oldId) game.winner = newId;
				if (game.stats[oldId]) {
					game.stats[newId] = game.stats[oldId];
					delete game.stats[oldId];
				}
			}
		}
	}
	return player;
}

/** Moves a player to a position (from 1) in the draft order */
export function movePlayer(league: DraftLeague, name: string, position: number) {
	const player = requirePlayer(league, name);
	if (league.phase !== 'setup') {
		throw new LeagueError(`The draft order can only be changed before the draft starts.`);
	}
	if (!Number.isInteger(position) || position < 1 || position > league.players.length) {
		throw new LeagueError(`The position must be from 1 to ${league.players.length}.`);
	}
	league.players.splice(league.players.indexOf(player), 1);
	league.players.splice(position - 1, 0, player);
}

/* The draft */

export function getTotalPicks(league: DraftLeague) {
	return league.players.length * league.settings.picks;
}

/** Whose turn it is at a point in the draft order, or null once the order is done */
export function getPicker(league: DraftLeague, pickNumber = league.pickNumber) {
	const count = league.players.length;
	if (!count || pickNumber >= getTotalPicks(league)) return null;
	const round = Math.floor(pickNumber / count);
	let index = pickNumber % count;
	if (league.settings.snake && round % 2 === 1) index = count - 1 - index;
	return league.players[index];
}

export function getPoints(league: DraftLeague, speciesid: string) {
	return league.settings.type === 'points' ? league.pool[speciesid] ?? 0 : 0;
}

export function getPointsUsed(league: DraftLeague, player: DraftPlayer) {
	return player.roster.reduce((total, id) => total + getPoints(league, id), 0);
}

export function getBudgetLeft(league: DraftLeague, player: DraftPlayer) {
	return league.settings.budget - getPointsUsed(league, player);
}

/** Who has drafted a Pokémon, if anyone */
export function getOwner(league: DraftLeague, speciesid: string) {
	return league.players.find(player => player.roster.includes(speciesid)) || null;
}

export function getAvailable(league: DraftLeague) {
	const taken = new Set(league.players.flatMap(player => player.roster));
	return Object.keys(league.pool).filter(id => !taken.has(id as string)) ;
}

/** Finds a Pokémon in the league's pool by name (a forme not in the pool counts as its base species, if that is) */
export function findPoolSpecies(league: DraftLeague, name: string) {
	const species = resolveSpecies(name);
	const ids = species ? [species.id, toID(name), species.baseId] : [toID(name)];
	const id = ids.find(i => i in league.pool);
	if (!id) throw new LeagueError(`${species ? species.name : name.trim()} isn't in this draft's pool.`);
	return id;
}

/** Throws if a player can't draft a Pokémon now */
export function checkPick(league: DraftLeague, player: DraftPlayer, speciesid: string) {
	if (!(speciesid in league.pool)) throw new LeagueError(`${speciesName(speciesid)} isn't in this draft's pool.`);
	const owner = getOwner(league, speciesid);
	if (owner) throw new LeagueError(`${speciesName(speciesid)} has already been drafted by ${owner.name}.`);
	if (player.roster.length >= league.settings.picks) {
		throw new LeagueError(`${player.name} already has ${league.settings.picks} Pokémon.`);
	}
	if (league.settings.type !== 'points') return;
	const cost = getPoints(league, speciesid);
	const left = getBudgetLeft(league, player);
	// keep enough points to fill the rest of the roster with the cheapest Pokémon left
	const picksAfter = league.settings.picks - player.roster.length - 1;
	let cheapest = Infinity;
	for (const id of getAvailable(league)) {
		if (id !== speciesid) cheapest = Math.min(cheapest, getPoints(league, id));
	}
	const reserve = picksAfter > 0 && cheapest !== Infinity ? cheapest * picksAfter : 0;
	if (cost > left) {
		throw new LeagueError(`${speciesName(speciesid)} costs ${cost} points, but ${player.name} only has ${left} left.`);
	}
	if (cost > left - reserve) {
		throw new LeagueError(
			`${player.name} can't afford ${speciesName(speciesid)} (${cost} points): they need to keep ${reserve} of their ` +
			`${left} points to fill the rest of their roster.`
		);
	}
}

/** Drafts a Pokémon: on the player's turn, or as one of the picks they're owed */
export function makePick(league: DraftLeague, player: DraftPlayer, speciesid: string) {
	if (league.phase !== 'draft') throw new LeagueError(`The draft isn't running.`);
	const onTheClock = getPicker(league);
	const owed = onTheClock?.id !== player.id;
	if (owed && !player.owedPicks) {
		throw new LeagueError(onTheClock ? `It's ${onTheClock.name}'s turn to pick.` : `All the picks have been made.`);
	}
	checkPick(league, player, speciesid);
	player.roster.push(speciesid);
	const pick: DraftPick = { player: player.id, species: speciesid, points: getPoints(league, speciesid) };
	if (owed) {
		pick.owed = true;
		player.owedPicks--;
	} else {
		league.pickNumber++;
	}
	league.picks.push(pick);
	return pick;
}

/** Skips whoever is on the clock; they can make the pick later */
export function skipPick(league: DraftLeague) {
	const player = getPicker(league);
	if (league.phase !== 'draft' || !player) throw new LeagueError(`No one is on the clock.`);
	player.owedPicks++;
	league.pickNumber++;
	league.picks.push({ player: player.id, species: '', points: 0, skipped: true });
	return player;
}

/** Takes back the last pick or skip */
export function undoPick(league: DraftLeague) {
	if (league.phase !== 'draft') throw new LeagueError(`The draft isn't running.`);
	const last = league.picks.pop();
	if (!last) throw new LeagueError(`There's nothing to undo.`);
	const player = getPlayer(league, last.player)!;
	if (last.skipped) {
		player.owedPicks--;
		league.pickNumber--;
		return last;
	}
	player.roster.splice(player.roster.lastIndexOf(last.species), 1);
	player.captains = player.captains.filter(id => id !== last.species);
	if (last.owed) {
		player.owedPicks++;
	} else {
		league.pickNumber--;
	}
	return last;
}

export function isDraftComplete(league: DraftLeague) {
	return !getPicker(league) && league.players.every(player => !player.owedPicks);
}

/* Tera Captains */

export function toggleCaptain(league: DraftLeague, player: DraftPlayer, speciesid: string) {
	if (!league.settings.tera) throw new LeagueError(`This draft doesn't use Tera Captains.`);
	if (!player.roster.includes(speciesid)) {
		throw new LeagueError(`${speciesName(speciesid)} isn't on ${player.name}'s roster.`);
	}
	if (player.captains.includes(speciesid)) {
		player.captains = player.captains.filter(id => id !== speciesid);
		return false;
	}
	if (player.captains.length >= league.settings.teraCaptains) {
		throw new LeagueError(`${player.name} already has ${count(league.settings.teraCaptains, "Tera Captains")}.`);
	}
	const maxPoints = league.settings.teraCaptainMaxPoints;
	if (maxPoints && getPoints(league, speciesid) > maxPoints) {
		throw new LeagueError(`Tera Captains can cost at most ${maxPoints} points.`);
	}
	player.captains.push(speciesid);
	return true;
}

/* The season */

/** A round-robin (circle method): every player plays every other player once. null is a bye. */
export function roundRobin(ids: string[]) {
	const list: (string | null)[] = [...ids];
	if (list.length % 2) list.push(null);
	const rounds: [string | null, string | null][][] = [];
	for (let r = 0; r < list.length - 1; r++) {
		const round: [string | null, string | null][] = [];
		for (let i = 0; i < list.length / 2; i++) {
			const a = list[i]!;
			const b = list[list.length - 1 - i]!;
			// alternate who's player 1, so it's not always the same people
			round.push(r % 2 ? [b, a] : [a, b]);
		}
		rounds.push(round);
		list.splice(1, 0, list.pop()!);
	}
	return rounds;
}

/** The weeks of matches; a season longer than a round-robin plays it again, with sides swapped */
export function generateSchedule(ids: string[], weeks: number): DraftMatch[][] {
	const rounds = roundRobin(ids);
	const schedule: DraftMatch[][] = [];
	for (let w = 0; w < weeks && rounds.length; w++) {
		const swap = Math.floor(w / rounds.length) % 2 === 1;
		schedule.push(rounds[w % rounds.length]!.map(([a, b]) => {
			if (swap) [a, b] = [b, a];
			// byes always have the player as p1
			if (!a) [a, b] = [b, a];
			return { week: w + 1, p1: a!, p2: b };
		}));
	}
	return schedule;
}

export function startSeason(league: DraftLeague) {
	if (league.players.length < 2) throw new LeagueError(`A season needs at least two players.`);
	league.phase = 'season';
	league.pickDeadline = null;
	league.schedule = generateSchedule(league.players.map(player => player.id), league.settings.weeks);
	league.week = 1;
}

export function getMatch(league: DraftLeague, week: number, playerid: string) {
	return league.schedule[week - 1]?.find(match => match.p1 === playerid || match.p2 === playerid) || null;
}

export function getOpponent(match: DraftMatch, playerid: string) {
	return match.p1 === playerid ? match.p2 : match.p1;
}

export function recordResult(league: DraftLeague, match: DraftMatch, result: MatchResult) {
	match.result = result;
	match.room = null;
	match.live = null;
}

export function getStandings(league: DraftLeague) {
	const standings = new Map<string, Standing>();
	for (const player of league.players) {
		standings.set(player.id, {
			id: player.id, name: player.name, played: 0, wins: 0, losses: 0, ties: 0,
			gameWins: 0, gameLosses: 0, kills: 0, deaths: 0,
		});
	}
	for (const match of league.schedule.flat()) {
		if (!match.result || !match.p2) continue;
		const { winner, score, games } = match.result;
		for (const [i, playerid] of [match.p1, match.p2].entries()) {
			const standing = standings.get(playerid);
			if (!standing) continue;
			standing.played++;
			if (!winner) standing.ties++;
			else if (winner === playerid) standing.wins++;
			else standing.losses++;
			standing.gameWins += score[i]!;
			standing.gameLosses += score[1 - i]!;
			for (const game of games) {
				for (const stats of Object.values(game.stats[playerid] || {})) {
					standing.kills += stats.kills;
					standing.deaths += stats.deaths;
				}
			}
		}
	}
	return sortBy([...standings.values()], s => [
		-s.wins, s.losses, -(s.kills - s.deaths), -(s.gameWins - s.gameLosses), toID(s.name),
	]);
}

/** Kills and deaths for every drafted Pokémon over the season, by who owns it now */
export function getPokemonStats(league: DraftLeague) {
	const totals = new Map<string, { species: string, player: string, games: number, kills: number, deaths: number }>();
	for (const match of league.schedule.flat()) {
		for (const game of match.result?.games || []) {
			for (const [playerid, byPokemon] of Object.entries(game.stats)) {
				for (const [speciesid, stats] of Object.entries(byPokemon)) {
					const key = `${playerid}:${speciesid}` as string;
					const total = totals.get(key) || { species: speciesid, player: playerid as string, games: 0, kills: 0, deaths: 0 };
					total.games++;
					total.kills += stats.kills;
					total.deaths += stats.deaths;
					totals.set(key, total);
				}
			}
		}
	}
	return sortBy([...totals.values()], t => [-t.kills, t.deaths, -t.games]);
}

/* Trades and free agents */

function checkRosterBudget(league: DraftLeague, player: DraftPlayer, roster: string[]) {
	if (league.settings.type !== 'points') return;
	const total = roster.reduce((sum, id) => sum + getPoints(league, id), 0);
	if (total > league.settings.budget) {
		throw new LeagueError(
			`That would put ${player.name} at ${total} points, over the budget of ${league.settings.budget}.`
		);
	}
}

function checkCanTransact(league: DraftLeague) {
	if (league.phase !== 'season') throw new LeagueError(`Trades and free agents are only open during the season.`);
}

export function offerTrade(league: DraftLeague, from: DraftPlayer, to: DraftPlayer, give: string[], get: string[]) {
	checkCanTransact(league);
	if (from === to) throw new LeagueError(`You can't trade with yourself.`);
	if (!give.length || !get.length) throw new LeagueError(`A trade needs Pokémon from both players.`);
	for (const id of give) {
		if (!from.roster.includes(id)) throw new LeagueError(`${speciesName(id)} isn't on ${from.name}'s roster.`);
	}
	for (const id of get) {
		if (!to.roster.includes(id)) throw new LeagueError(`${speciesName(id)} isn't on ${to.name}'s roster.`);
	}
	for (const player of [from, to]) {
		if (player.tradesUsed >= league.settings.trades) {
			throw new LeagueError(`${player.name} has no trades left.`);
		}
	}
	checkRosterBudget(league, from, [...from.roster.filter(id => !give.includes(id)), ...get]);
	checkRosterBudget(league, to, [...to.roster.filter(id => !get.includes(id)), ...give]);
	const trade: DraftTrade = { id: league.nextTradeId++, from: from.id, to: to.id, give, get, status: 'pending' };
	league.trades.push(trade);
	return trade;
}

/** A trade that's still open: offered (pending), or accepted and waiting for staff (agreed) */
export function getTrade(league: DraftLeague, id: number, statuses: TradeStatus[] = ['pending']) {
	const trade = league.trades.find(t => t.id === id);
	if (!trade || !statuses.includes(trade.status)) {
		throw new LeagueError(statuses.includes('agreed') && !statuses.includes('pending') ?
			`That trade isn't waiting for approval.` : `That trade offer isn't open.`);
	}
	return trade;
}

/** Checks a trade can still happen (rosters may have changed since it was offered), returning the new rosters */
function checkTrade(league: DraftLeague, trade: DraftTrade) {
	checkCanTransact(league);
	const from = requirePlayer(league, trade.from);
	const to = requirePlayer(league, trade.to);
	for (const id of trade.give) {
		if (!from.roster.includes(id)) throw new LeagueError(`${from.name} no longer has ${speciesName(id)}.`);
	}
	for (const id of trade.get) {
		if (!to.roster.includes(id)) throw new LeagueError(`${to.name} no longer has ${speciesName(id)}.`);
	}
	for (const player of [from, to]) {
		if (player.tradesUsed >= league.settings.trades) throw new LeagueError(`${player.name} has no trades left.`);
	}
	const fromRoster = [...from.roster.filter(id => !trade.give.includes(id)), ...trade.get];
	const toRoster = [...to.roster.filter(id => !trade.get.includes(id)), ...trade.give];
	checkRosterBudget(league, from, fromRoster);
	checkRosterBudget(league, to, toRoster);
	return { from, to, fromRoster, toRoster };
}

/** The other player accepts a trade offer; it then waits for room staff to approve it */
export function agreeTrade(league: DraftLeague, trade: DraftTrade) {
	checkTrade(league, trade);
	trade.status = 'agreed';
}

/** Room staff approve an agreed trade, which makes it happen */
export function approveTrade(league: DraftLeague, trade: DraftTrade) {
	if (trade.status !== 'agreed') throw new LeagueError(`That trade isn't waiting for approval.`);
	const { from, to, fromRoster, toRoster } = checkTrade(league, trade);
	from.roster = fromRoster;
	to.roster = toRoster;
	// Tera Captains traded away stop being captains
	from.captains = from.captains.filter(id => fromRoster.includes(id));
	to.captains = to.captains.filter(id => toRoster.includes(id));
	from.tradesUsed++;
	to.tradesUsed++;
	trade.status = 'approved';
	league.transactions.push(
		`Week ${league.week}: ${from.name} traded ${trade.give.map(speciesName).join(', ')} to ${to.name} for ` +
		`${trade.get.map(speciesName).join(', ')}.`
	);
	cancelTradesInvolving(league, [...trade.give, ...trade.get]);
}

/** Open trades for Pokémon that have changed hands can't happen any more */
function cancelTradesInvolving(league: DraftLeague, moved: string[]) {
	for (const other of league.trades) {
		if (other.status !== 'pending' && other.status !== 'agreed') continue;
		if ([...other.give, ...other.get].some(id => moved.includes(id))) other.status = 'cancelled';
	}
}

export function swapFreeAgent(league: DraftLeague, player: DraftPlayer, drop: string, add: string) {
	checkCanTransact(league);
	if (player.freeAgentsUsed >= league.settings.freeAgents) {
		throw new LeagueError(`${player.name} has no free agent pickups left.`);
	}
	if (!player.roster.includes(drop)) {
		throw new LeagueError(`${speciesName(drop)} isn't on ${player.name}'s roster.`);
	}
	if (!(add in league.pool)) throw new LeagueError(`${speciesName(add)} isn't in this draft's pool.`);
	const owner = getOwner(league, add);
	if (owner) throw new LeagueError(`${speciesName(add)} is on ${owner.name}'s roster.`);
	const roster = player.roster.map(id => id === drop ? add : id);
	checkRosterBudget(league, player, roster);
	player.roster = roster;
	player.captains = player.captains.filter(id => id !== drop);
	player.freeAgentsUsed++;
	league.transactions.push(`Week ${league.week}: ${player.name} dropped ${speciesName(drop)} and picked up ${speciesName(add)}.`);
	cancelTradesInvolving(league, [drop]);
}
